import { idbGet, idbPut, STORES } from './database';

/**
 * Lifetime token/cost benchmarks for this browser profile ("this device"). Persisted locally
 * regardless of login state — signing in additionally syncs it to the server (see
 * src/account/sync.ts) so it can be aggregated with a user's other devices.
 */
export interface BenchmarkStats {
  requests: number;
  tokensBefore: number; // estimated unoptimized size, summed across requests
  tokensAfter: number; // actual (optimized) size, summed across requests
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  costUsd: number;
}

const KEY_BENCHMARKS = 'cumulativeBenchmarks';

export function emptyBenchmarkStats(): BenchmarkStats {
  return {
    requests: 0,
    tokensBefore: 0,
    tokensAfter: 0,
    inputTokens: 0,
    outputTokens: 0,
    cacheReadTokens: 0,
    cacheWriteTokens: 0,
    costUsd: 0,
  };
}

export async function getCumulativeBenchmarkStats(): Promise<BenchmarkStats> {
  return (await idbGet<BenchmarkStats>(STORES.settings, KEY_BENCHMARKS)) ?? emptyBenchmarkStats();
}

export async function saveCumulativeBenchmarkStats(stats: BenchmarkStats): Promise<void> {
  await idbPut(STORES.settings, stats, KEY_BENCHMARKS);
}

export function addBenchmarkDelta(base: BenchmarkStats, delta: Partial<BenchmarkStats>): BenchmarkStats {
  return {
    requests: base.requests + (delta.requests ?? 0),
    tokensBefore: base.tokensBefore + (delta.tokensBefore ?? 0),
    tokensAfter: base.tokensAfter + (delta.tokensAfter ?? 0),
    inputTokens: base.inputTokens + (delta.inputTokens ?? 0),
    outputTokens: base.outputTokens + (delta.outputTokens ?? 0),
    cacheReadTokens: base.cacheReadTokens + (delta.cacheReadTokens ?? 0),
    cacheWriteTokens: base.cacheWriteTokens + (delta.cacheWriteTokens ?? 0),
    costUsd: base.costUsd + (delta.costUsd ?? 0),
  };
}

/** Loads, applies a delta, persists, and returns the new cumulative stats — one call per request. */
export async function recordBenchmarkDelta(delta: Partial<BenchmarkStats>): Promise<BenchmarkStats> {
  const current = await getCumulativeBenchmarkStats();
  const next = addBenchmarkDelta(current, delta);
  await saveCumulativeBenchmarkStats(next);
  return next;
}
